import React, { useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import fetchAllCentersWithIds from "../List/CenterList";
import { toast } from "react-toastify";
import api from "../../config/URL";
import { Link } from "react-router-dom";
import fetchAllCoursesWithIds from "../List/CourseList";
import fetchAllLevelsWithIds from "../List/LevelList";

function StudentSlotCountReports({ selectedCenter }) {
  const getCurrentWeek = () => {
    const date = new Date();
    date.setDate(date.getDate() + 4 - (date.getDay() || 7));
    const yearStart = new Date(date.getFullYear(), 0, 1);
    const weekNumber = Math.ceil(
      ((date - yearStart) / 86400000 + yearStart.getDay() + 1) / 7
    );
    const isoYear = date.getFullYear();
    return `${isoYear}-W${String(weekNumber).padStart(2, "0")}`;
  };

  const [selectedType, setSelectedType] = useState(getCurrentWeek());
  const [centerData, setCenterData] = useState(null);
  const [coursesData, setCoursesData] = useState(null);
  const [levelsData, setLevelsData] = useState(null);
  const [selectedCenterId, setSelectedCenterId] = useState(selectedCenter);
  const [selectedDay, setSelectedDay] = useState("ALL");

  // New filters for Course and Level
  const [filters, setFilters] = useState({
    courseId: "",
    levelId: "",
  });

  const [chartData, setChartData] = useState({
    series: [],
    labels: [],
  });

  const fetchData = async () => {
    try {
      const centerData = await fetchAllCentersWithIds();
      setCenterData(centerData);
      setSelectedCenterId(selectedCenter);
    } catch (error) {
      toast.error(error);
    }
  };

  const fetchCoursesData = async () => {
    try {
      const course = await fetchAllCoursesWithIds();
      setCoursesData(course);
    } catch (error) {
      toast.error(error.message || "Error fetching course");
    }
  };

  const fetchLevelData = async () => {
    try {
      const level = await fetchAllLevelsWithIds();
      setLevelsData(level);
    } catch (error) {
      toast.error(error.message || "Error fetching Level");
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedCenter]);

  useEffect(() => {
    fetchCoursesData();
    fetchLevelData();
  }, []);

  const handleDayChange = (e) => {
    setSelectedDay(e.target.value);
  };

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const fetchEnrollmentData = async (centerId, week, day, courseId = "", levelId = "") => {
    try {
      const queryParams = new URLSearchParams({
        centerId: centerId,
        week: week,
        day: day,
      });

      // Only add courseId and levelId if they are selected (not empty)
      if (courseId) queryParams.append("courseId", courseId);
      if (levelId) queryParams.append("levelId", levelId);

      const response = await api.get(
        `/globalStudentSlotCountReport?${queryParams.toString()}`,
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      const data = response.data;
      let labels = [];
      let variance = [];
      let currentTotal = [];

      if (Array.isArray(data.dayData)) {
        const dayObject = data.dayData[0] || {};
        labels = data.labels || [];
        variance = labels.map((label) => dayObject[label]?.bookedSlot || 0);
        currentTotal = labels.map((label) => dayObject[label]?.availableSlot || 0);
      } else {
        const dayObject = data.dayData || {};
        labels = data.labels || [];
        variance = labels.map((label) => dayObject[label]?.bookedSlot || 0);
        currentTotal = labels.map((label) => dayObject[label]?.availableSlot || 0);
      }

      setChartData({
        series: [
          { name: "Booked Slots", data: variance },
          { name: "Available Slots", data: currentTotal },
        ],
        labels: labels,
      });
    } catch (error) {
      toast.error("Error fetching data");
      console.error(error);
    }
  };

  // Trigger API when any filter changes
  useEffect(() => {
    if (selectedCenterId) {
      fetchEnrollmentData(
        selectedCenterId,
        selectedType,
        selectedDay,
        filters.courseId,
        filters.levelId
      );
    }
  }, [selectedType, selectedDay, selectedCenterId, filters.courseId, filters.levelId]);

  const options = {
    chart: {
      type: "bar",
      stacked: true,
      stackType: "100%",
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "45%",
        borderRadius: 2,
        dataLabels: { position: "center" },
      },
    },
    xaxis: {
      categories: chartData.labels,
      labels: { style: { fontSize: "12px" } },
    },
    yaxis: {
      labels: {
        formatter: (val) => `${val}%`,
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val, opts) {
        const seriesIndex = opts.seriesIndex;
        const dataIndex = opts.dataPointIndex;
        return chartData.series[seriesIndex]?.data[dataIndex] || 0;
      },
      style: {
        colors: ["#fff"],
        fontWeight: "bold",
      },
    },
    tooltip: {
      y: {
        formatter: (val, opts) => {
          const count = chartData.series[opts.seriesIndex]?.data[opts.dataPointIndex] || 0;
          return `${count} slots`;
        },
      },
    },
    colors: ["#E53935", "#3949AB"],
    legend: {
      position: "top",
      horizontalAlign: "center",
      fontSize: "13px",
    },
    grid: {
      borderColor: "#f1f1f1",
      strokeDashArray: 3,
    },
  };

  return (
    <div className="d-flex flex-column align-items-center justify-content-center Hero">
      <div className="container">
        <ol className="breadcrumb my-3" style={{ listStyle: "none", padding: 0, margin: 0 }}>
          <li>
            <Link to="/" className="custom-breadcrumb">Home</Link>
            <span className="breadcrumb-separator"> &gt; </span>
          </li>
          <li>
            Report Management
            <span className="breadcrumb-separator"> &gt; </span>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            Student Slot Count Report
          </li>
        </ol>

        <div className="card">
          <div
            className="d-flex px-4 justify-content-between align-items-center p-1"
            style={{ background: "#f5f7f9" }}
          >
            <div className="d-flex align-items-center">
              <div className="d-flex">
                <div className="dot active"></div>
              </div>
              <span className="me-2 text-muted">Student Slot Count Report</span>
            </div>
          </div>

          <div className="container">
            <div className="row my-2">
              <div className="col-md-3 col-12 mb-3">
                <label className="form-label">Week</label>
                <input
                  type="week"
                  className="form-control"
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                />
              </div>

              <div className="col-md-3 col-12 mb-3">
                <label className="form-label">Day</label>
                <select
                  className="form-select"
                  onChange={handleDayChange}
                  value={selectedDay}
                >
                  <option value="ALL">ALL</option>
                  <option value="SUNDAY">SUNDAY</option>
                  <option value="MONDAY">MONDAY</option>
                  <option value="TUESDAY">TUESDAY</option>
                  <option value="WEDNESDAY">WEDNESDAY</option>
                  <option value="THURSDAY">THURSDAY</option>
                  <option value="FRIDAY">FRIDAY</option>
                  <option value="SATURDAY">SATURDAY</option>
                </select>
              </div>

              <div className="col-md-3 col-12 mb-3">
                <label className="form-label">Course</label>
                <select
                  className="form-select"
                  value={filters.courseId}
                  onChange={(e) => handleFilterChange("courseId", e.target.value)}
                >
                  <option value="">All Courses</option>
                  {coursesData?.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.courseNames}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-3 col-12 mb-3">
                <label className="form-label">Level</label>
                <select
                  className="form-select"
                  value={filters.levelId}
                  onChange={(e) => handleFilterChange("levelId", e.target.value)}
                >
                  <option value="">All Levels</option>
                  {levelsData?.map((level) => (
                    <option key={level.id} value={level.id}>
                      {level.levels}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="card p-4 mb-4">
              <div className="row">
                <div className="col-12">
                  {chartData.labels.length > 0 ? (
                    <div className="capacity-chart">
                      <ReactApexChart
                        options={options}
                        series={chartData.series}
                        type="bar"
                        height={400}
                      />
                    </div>
                  ) : (
                    <div className="text-center py-5 text-muted">
                      No data available for the selected filters.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StudentSlotCountReports;